import { fg } from '@atlaskit/platform-feature-flags';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { getConfluenceDatabaseClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-database-client';
import { getConfluenceSlidesClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-slides-client';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { getConfluenceWhiteboardClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-whiteboard-client';

export function objectHasProperties(data: unknown, props: string[]): boolean {
	return (
		data !== null && typeof data === 'object' && props.every((prop) => data.hasOwnProperty(prop))
	);
}

// ============================================================================
// DYNAMIC UI CREATE
// ============================================================================
export const DYNAMIC_UI_CREATE_KEY = 'dynamic-ui-create';

export type DynamicUiCreateDataPayload = {
	id: string;
};

export type DynamicUiUpdateDataPayload = {
	id: string;
	contentId: string;
	previousMauiId: string;
	contentType: string;
};

export function isDynamicUiCreateData(data: unknown): data is DynamicUiCreateDataPayload {
	return objectHasProperties(data, ['id']);
}

export function isDynamicUiUpdateActionData(data: unknown): data is DynamicUiUpdateDataPayload {
	return objectHasProperties(data, ['id', 'previousMauiId', 'contentType']);
}

export function isDynamicUiCreateAction(
	action: MessageAction,
): action is MessageAction & { data: DynamicUiCreateDataPayload } {
	return isDynamicUiCreateData(action.data);
}

export const buildDynamicUiPageUrl = (data: DynamicUiCreateDataPayload): string | null => {
	if (data?.id) {
		return `${window.location.origin}/remix/ui/${encodeURIComponent(data.id)}`;
	}
	return null;
};

// ============================================================================
// IMAGE CREATE
// ============================================================================
export const IMAGE_CREATE_KEY = 'image-create';
export function isImageCreateData(data: unknown): data is ImageCreateDataPayload {
	return typeof data === 'object' && data !== null && 'mediaFileId' in data && 'mediaUrl' in data;
}
export type ImageCreateDataPayload = {
	mediaFileId: string;
	mediaUrl: string;
};

const MEDIA_BASE_URL_STAGING = 'https://media.staging.atl-paas.net';
const MEDIA_BASE_URL_PROD = 'https://media.prod.atl-paas.net';
export const buildImageUrl = ({
	fileId,
	token,
	collection,
	clientId,
	isStaging,
}: {
	fileId: string;
	token: string;
	collection: string;
	clientId: string;
	isStaging: boolean;
}): string => {
	const baseUrl = isStaging ? MEDIA_BASE_URL_STAGING : MEDIA_BASE_URL_PROD;
	const url = new URL(`${baseUrl}/file/${fileId}/image`);
	url.searchParams.set('token', token);
	url.searchParams.set('issuer', 'micros/convo-ai');
	url.searchParams.set('collection', collection);
	url.searchParams.set('client', clientId);
	return url.toString();
};

// ============================================================================
// CONFLUENCE AIFC PAGE CREATE
// ============================================================================
export const CONFLUENCE_AIFC_PAGE_CREATE_KEY = 'aifc-confluence-page-create';

export type ConfluenceAIFCPageCreateActionPayload = ConfluenceAIFCAccumulatedData & {
	title: string;
	adf: string;
	contentId: string;
};

export type AIFCActionCompletePayload = {
	contentId: string;
};

export function isConfluenceAIFCPageCreateActionData(
	data: unknown,
): data is ConfluenceAIFCPageCreateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'title' in data &&
		'adf' in data &&
		'contentId' in data
	);
}

// ----------------------------------------------------------------------------
// Deep Research Confluence Page Create Processor (non-bridge processor key)
// ----------------------------------------------------------------------------
export const CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY =
	'aifc-deep-research-confluence-page-create';

export type ConfluenceAIFCDeepResearchPageCreateActionPayload = ConfluenceAIFCAccumulatedData & {
	title: string;
	content: string;
	contentId: string;
};

export function isConfluenceAIFCDeepResearchPageCreateActionData(
	data: unknown,
): data is ConfluenceAIFCDeepResearchPageCreateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'title' in data &&
		'content' in data &&
		'contentId' in data
	);
}

export function isConfluenceAIFCDeepResearchPageCreateActionPrepare(
	data: unknown,
): data is ConfluenceAIFCDeepResearchPageCreateActionPayload {
	return typeof data === 'object' && data !== null && 'contentId' in data;
}

// ============================================================================
// CONFLUENCE AIFC PAGE UPDATE
// ============================================================================
export const CONFLUENCE_AIFC_PAGE_UPDATE_KEY = 'aifc-confluence-page-update';
export const AIFC_ADF_UPDATE_KEY = 'aifc-adf-update';
export type ConfluenceAIFCPageUpdateActionPayload = {
	document: string;
	contentId: string;
};

export type AIFCADFUpdateActionPayload = {
	toolCalls: string;
	contentId: string;
};

export function isConfluenceAIFCPageUpdateActionData(
	data: unknown,
): data is ConfluenceAIFCPageUpdateActionPayload {
	return typeof data === 'object' && data !== null && 'document' in data && 'contentId' in data;
}

export function isAIFCADFUpdateActionData(data: unknown): data is AIFCADFUpdateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'toolCalls' in data &&
		'contentId' in data &&
		typeof data.toolCalls === 'string' &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC WHITEBOARD CREATE
// ============================================================================
export const CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY = 'aifc-confluence-whiteboard-create';

export type ConfluenceAIFCWhiteboardCreateActionPayload = ConfluenceAIFCAccumulatedData & {
	document: string;
	title: string;
	contentId: string;
};

export function isConfluenceAIFCWhiteboardCreateActionData(
	data: unknown,
): data is ConfluenceAIFCWhiteboardCreateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'document' in data &&
		'title' in data &&
		typeof data.title === 'string' &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC SLIDES CREATE
// ============================================================================
export const CONFLUENCE_AIFC_SLIDES_CREATE_KEY = 'aifc-confluence-slides-create';

export type ConfluenceAIFCSlidesCreateActionPayload = ConfluenceAIFCAccumulatedData & {
	document: string;
	title: string;
	contentId: string;
};

export function isConfluenceAIFCSlidesCreateActionData(
	data: unknown,
): data is ConfluenceAIFCSlidesCreateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'document' in data &&
		'title' in data &&
		typeof data.title === 'string' &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC SLIDES UPDATE
// ============================================================================
export const CONFLUENCE_AIFC_SLIDES_UPDATE_KEY = 'aifc-confluence-slides-update';

export type ConfluenceAIFCSlidesUpdateActionPayload = {
	document: string;
	contentId: string;
};

export function isConfluenceAIFCSlidesUpdateActionData(
	data: unknown,
): data is ConfluenceAIFCSlidesUpdateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'document' in data &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC WHITEBOARD UPDATE
// ============================================================================
export const CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY = 'aifc-confluence-whiteboard-update';

export type ConfluenceAIFCWhiteboardUpdateActionPayload = {
	document: string;
	contentId: string;
};

export function isConfluenceAIFCWhiteboardUpdateActionData(
	data: unknown,
): data is ConfluenceAIFCWhiteboardUpdateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'document' in data &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC WHITEBOARD TO PAGE
// ============================================================================
export const CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY = 'aifc-confluence-whiteboard-to-page';

// ============================================================================
// CONFLUENCE WHITEBOARD BRAINSTORMING ACTION
// ============================================================================
export const CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY = 'confluence-whiteboard-brainstorming';

export type ConfluenceWhiteboardBrainstormingActionPayload = {
	content: string;
	contentId: string;
};

export function isConfluenceWhiteboardBrainstormingActionData(
	data: unknown,
): data is ConfluenceWhiteboardBrainstormingActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'content' in data &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE WHITEBOARD DIAGRAMMING ACTION
// ============================================================================
export const LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY = 'confluence-whiteboard-diagramming';

export const AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY = 'aifc-confluence-whiteboard-diagramming';

export type ConfluenceWhiteboardDiagrammingActionDiagramType =
	| 'FLOW_CHART'
	| 'MINDMAP'
	| 'STATE_DIAGRAM'
	| 'AFFINITY_DIAGRAM'
	| 'ARCHITECTURE_DIAGRAM';

export type ConfluenceWhiteboardDiagrammingActionPayload = {
	content: string;
	diagramType: ConfluenceWhiteboardDiagrammingActionDiagramType;
	contentId: string;
};

export function isConfluenceWhiteboardDiagrammingActionData(
	data: unknown,
): data is ConfluenceWhiteboardDiagrammingActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'content' in data &&
		'diagramType' in data &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC DATABASE CREATE
// ============================================================================
export const CONFLUENCE_AIFC_DATABASE_CREATE_KEY = 'aifc-confluence-database-create';

export type ConfluenceAIFCDatabaseCreateActionPayload = ConfluenceAIFCAccumulatedData & {
	document: string;
	title: string;
	contentId: string;
};

export function isConfluenceAIFCDatabaseCreateActionData(
	data: unknown,
): data is ConfluenceAIFCDatabaseCreateActionPayload {
	return (
		typeof data === 'object' &&
		data !== null &&
		'document' in data &&
		'title' in data &&
		typeof data.title === 'string' &&
		'contentId' in data &&
		typeof data.contentId === 'string'
	);
}

// ============================================================================
// CONFLUENCE AIFC DATABASE UPDATE
// ============================================================================
export const CONFLUENCE_AIFC_DATABASE_UPDATE_KEY = 'aifc-confluence-database-update';
export type ConfluenceAIFCDatabaseUpdateActionPayload = {
	document: string;
	contentId: string;
};
export function isConfluenceAIFCDatabaseUpdateActionData(
	data: unknown,
): data is ConfluenceAIFCDatabaseUpdateActionPayload {
	return typeof data === 'object' && data !== null && 'document' in data && 'contentId' in data;
}

// ============================================================================
// CONFLUENCE SPACE CREATE
// ============================================================================
export const CONFLUENCE_AIFC_SPACE_CREATE_KEY = 'aifc-confluence-space-create';

type ConfluenceAIFCSpaceCreateActionPayload = {
	jsonSpace: string;
};

export function isConfluenceAIFCSpaceCreateActionData(
	data: unknown,
): data is ConfluenceAIFCSpaceCreateActionPayload {
	return typeof data === 'object' && data !== null && 'jsonSpace' in data;
}

// ============================================================================
// CONFLUENCE SPACE UPDATE
// ============================================================================
export const CONFLUENCE_AIFC_SPACE_UPDATE_KEY = 'aifc-confluence-space-update';

type ConfluenceAIFCSpaceUpdateActionPayload = {
	jsonSpace: string;
};

export function isConfluenceAIFCSpaceUpdateActionData(
	data: unknown,
): data is ConfluenceAIFCSpaceUpdateActionPayload {
	return typeof data === 'object' && data !== null && 'jsonSpace' in data;
}

export const PAGE_CREATE_ACTIONS: Set<string> = new Set([
	CONFLUENCE_AIFC_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY,
	CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY,
]);
export const WHITEBOARD_CREATE_ACTIONS: Set<string> = new Set([
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
]);
export const SLIDES_CREATE_ACTIONS: Set<string> = new Set([CONFLUENCE_AIFC_SLIDES_CREATE_KEY]);
export const DATABASE_CREATE_ACTIONS: Set<string> = new Set([CONFLUENCE_AIFC_DATABASE_CREATE_KEY]);
const SPACE_CREATE_ACTIONS = new Set([CONFLUENCE_AIFC_SPACE_CREATE_KEY]);

const DYNAMIC_UI_KEYS = new Set([DYNAMIC_UI_CREATE_KEY, IMAGE_CREATE_KEY]);

const CREATABLE_ACTION_KEYS = new Set([
	...PAGE_CREATE_ACTIONS,
	...WHITEBOARD_CREATE_ACTIONS,
	...SLIDES_CREATE_ACTIONS,
	...DATABASE_CREATE_ACTIONS,
	...SPACE_CREATE_ACTIONS,
]);

const PAGE_UPDATE_ACTIONS = new Set([
	CONFLUENCE_AIFC_PAGE_UPDATE_KEY,
	AIFC_ADF_UPDATE_KEY,
	DYNAMIC_UI_CREATE_KEY,
]);
const WHITEBOARD_UPDATE_ACTIONS = new Set([
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY,
	LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
]);
const SLIDES_UPDATE_ACTIONS = new Set([CONFLUENCE_AIFC_SLIDES_UPDATE_KEY]);
const DATABASE_UPDATE_ACTIONS = new Set([CONFLUENCE_AIFC_DATABASE_UPDATE_KEY]);
const SPACE_UPDATE_ACTIONS = new Set([CONFLUENCE_AIFC_SPACE_UPDATE_KEY]);
const UPDATABLE_ACTION_KEYS = new Set([
	...PAGE_UPDATE_ACTIONS,
	...WHITEBOARD_UPDATE_ACTIONS,
	...SLIDES_UPDATE_ACTIONS,
	...DATABASE_UPDATE_ACTIONS,
	...SPACE_UPDATE_ACTIONS,
]);

export const isConfluencePageCreateAction = (action: { key: string }): boolean =>
	PAGE_CREATE_ACTIONS.has(action.key);

export const isConfluenceCreatableAction = (action: { key: string }): boolean =>
	CREATABLE_ACTION_KEYS.has(action.key);

/**
 * Confluence content creation targeting existing contentId
 * (e.g., filling a blank page)
 */
export const isConfluenceCreateExistingAction = (action: PreviewCardAction): boolean =>
	isConfluenceCreatableAction(action) &&
	!!action.data?.metadata?.isExistingContent &&
	fg('confluence_aifc_create_on_existing');

/**
 * Confluence create action with new contentId generation
 */
export const isConfluenceCreateNewAction = (action: PreviewCardAction): boolean =>
	isConfluenceCreatableAction(action) && !isConfluenceCreateExistingAction(action);

export const isDynamicUiCreatableAction = (action: { key: string }): boolean =>
	DYNAMIC_UI_KEYS.has(action.key);

export const isConfluencePageUpdateAction = (action: { key: string }): boolean =>
	PAGE_UPDATE_ACTIONS.has(action.key);

export const isConfluenceWhiteboardUpdateAction = (action: { key: string }): boolean =>
	WHITEBOARD_UPDATE_ACTIONS.has(action.key);

export const isConfluenceSlidesUpdateAction = (action: { key: string }): boolean =>
	SLIDES_UPDATE_ACTIONS.has(action.key);

export const isConfluenceDatabaseUpdateAction = (action: { key: string }): boolean =>
	DATABASE_UPDATE_ACTIONS.has(action.key);

export const isConfluenceUpdatableAction = (action: { key: string }): boolean =>
	UPDATABLE_ACTION_KEYS.has(action.key);

// All Confluence actions support streaming, so we can just check if it's a creatable or updatable action.
export const isConfluenceStreamAction = (action: { key: string }): boolean =>
	isConfluenceCreatableAction(action) || isConfluenceUpdatableAction(action);

// ============================================================================
// CONFLUENCE PREVIEW CARDS
// ============================================================================
const PREVIEW_CARD_ACTION_KEYS = new Set([
	CONFLUENCE_AIFC_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_UPDATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY,
	CONFLUENCE_AIFC_DATABASE_CREATE_KEY,
]);

export type PreviewCardAction = {
	key: string;
	data?: any;
};

export const isConfluencePreviewCardAction = (action: PreviewCardAction): boolean =>
	PREVIEW_CARD_ACTION_KEYS.has(action.key) && !isConfluenceCreateExistingAction(action);

// ============================================================================
// CONTENT PREVIEW IFRAME
// ============================================================================
export const generateRovoReceiverId = (
	action: { key: string },
	selectedPreviewContentId: string,
): string | undefined => {
	if (!action?.key) {
		return;
	}
	switch (action.key) {
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
			return getConfluenceWhiteboardClientId(selectedPreviewContentId);
		case CONFLUENCE_AIFC_SLIDES_CREATE_KEY:
		case CONFLUENCE_AIFC_SLIDES_UPDATE_KEY:
			return getConfluenceSlidesClientId(selectedPreviewContentId);
		case CONFLUENCE_AIFC_DATABASE_CREATE_KEY:
			return getConfluenceDatabaseClientId(selectedPreviewContentId);
		default:
			return getConfluenceEditorClientId(selectedPreviewContentId);
	}
};

export const getConfluenceContentType = (action: {
	key: string;
}):
	| 'confluence-page'
	| 'confluence-whiteboard'
	| 'confluence-slide'
	| 'confluence-database'
	| 'confluence-space'
	| undefined => {
	const actionKey = action.key;
	if (PAGE_CREATE_ACTIONS.has(actionKey) || PAGE_UPDATE_ACTIONS.has(actionKey)) {
		return 'confluence-page';
	}
	if (WHITEBOARD_CREATE_ACTIONS.has(actionKey) || WHITEBOARD_UPDATE_ACTIONS.has(actionKey)) {
		return 'confluence-whiteboard';
	}
	if (SLIDES_CREATE_ACTIONS.has(actionKey) || SLIDES_UPDATE_ACTIONS.has(actionKey)) {
		return 'confluence-slide';
	}
	if (DATABASE_CREATE_ACTIONS.has(actionKey) || DATABASE_UPDATE_ACTIONS.has(actionKey)) {
		return 'confluence-database';
	}
	if (SPACE_CREATE_ACTIONS.has(actionKey) || SPACE_UPDATE_ACTIONS.has(actionKey)) {
		return 'confluence-space';
	}
	return undefined;
};

// ============================================================================
// ACCUMULATED ACTION DATA
// ============================================================================

type ConfluenceAIFCAccumulatedData = {
	previousAccumulatedData?: string;
};

// This mapping should contain the type guards of those actions that support data
// accumulation as defined below in `getPrimaryAccumulatedValue`.
const CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS: {
	readonly 'aifc-confluence-page-create': typeof isConfluenceAIFCPageCreateActionData;
	readonly 'aifc-deep-research-confluence-page-create': typeof isConfluenceAIFCDeepResearchPageCreateActionData;
	readonly 'aifc-confluence-database-create': typeof isConfluenceAIFCDatabaseCreateActionData;
	readonly 'aifc-confluence-whiteboard-create': typeof isConfluenceAIFCWhiteboardCreateActionData;
	readonly 'aifc-confluence-slides-create': typeof isConfluenceAIFCSlidesCreateActionData;
} = {
	[CONFLUENCE_AIFC_PAGE_CREATE_KEY]: isConfluenceAIFCPageCreateActionData,
	[CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY]: isConfluenceAIFCDeepResearchPageCreateActionData,
	[CONFLUENCE_AIFC_DATABASE_CREATE_KEY]: isConfluenceAIFCDatabaseCreateActionData,
	[CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY]: isConfluenceAIFCWhiteboardCreateActionData,
	[CONFLUENCE_AIFC_SLIDES_CREATE_KEY]: isConfluenceAIFCSlidesCreateActionData,
} as const;

export const isAccumulatedAction = (
	key: string,
): key is keyof typeof CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS => {
	return key in CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS;
};

export const getAccumulatedActionTypeGuard = (
	key: keyof typeof CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS,
): (typeof CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS)[keyof typeof CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS] => {
	return CONFLUENCE_AIFC_ACCUMULATED_DATA_TYPE_GUARDS[key];
};

export const getPrimaryAccumulatedValue = (
	action: MessageAction,
): { key: string; value: string } | undefined => {
	if (!action.data) {
		return undefined;
	}

	if (
		action.key === CONFLUENCE_AIFC_PAGE_CREATE_KEY &&
		isConfluenceAIFCPageCreateActionData(action.data)
	) {
		const key = 'adf';
		return { key, value: action.data[key] };
	} else if (
		action.key === CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY &&
		isConfluenceAIFCDeepResearchPageCreateActionData(action.data)
	) {
		const key = 'content';
		return { key, value: action.data[key] };
	} else if (
		action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY &&
		isConfluenceAIFCWhiteboardCreateActionData(action.data)
	) {
		const key = 'document';
		return { key, value: action.data[key] };
	} else if (
		action.key === CONFLUENCE_AIFC_SLIDES_CREATE_KEY &&
		isConfluenceAIFCSlidesCreateActionData(action.data)
	) {
		const key = 'document';
		return { key, value: action.data[key] };
	} else if (
		action.key === CONFLUENCE_AIFC_DATABASE_CREATE_KEY &&
		isConfluenceAIFCDatabaseCreateActionData(action.data)
	) {
		const key = 'document';
		return { key, value: action.data[key] };
	}

	return undefined;
};

const getAccumulatedData = (
	prevAction: MessageAction,
	nextAction: MessageAction,
): Record<string, string> | undefined => {
	const prevPrimary = getPrimaryAccumulatedValue(prevAction);
	const nextPrimary = getPrimaryAccumulatedValue(nextAction);

	if (!prevPrimary || !nextPrimary || prevPrimary.key !== nextPrimary.key) {
		return undefined;
	}

	return { [nextPrimary.key]: String(prevPrimary.value ?? '') + String(nextPrimary.value ?? '') };
};

export const customActionUpdater = (
	prevAction: MessageAction,
	nextAction: MessageAction,
): MessageAction => {
	if (prevAction.data && nextAction.data) {
		const accumulatedData = getAccumulatedData(prevAction, nextAction);
		if (accumulatedData !== undefined) {
			const updatedData = {
				...nextAction.data,
				...accumulatedData,
			};
			return { ...prevAction, data: updatedData, status: nextAction.status };
		}
	}

	return { ...prevAction, data: nextAction.data, status: nextAction.status };
};
